import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import useKount from '../hooks/useKount';
import ProcessingTimeline from '../components/ProcessingTimeline';

const BASE = '/server/equifax_function/api';

async function publicRequest(path, body) {
  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  });
  const data = await res.json();
  if (!res.ok) {
    const err = new Error(data.error || `Request failed (${res.status})`);
    err.debugSteps = data.debugSteps || [];
    err.alreadyProcessed = data.alreadyProcessed || false;
    err.alreadyPooled = data.alreadyPooled || false;
    err.clientName = data.clientName || '';
    err.leadSource = data.leadSource || '';
    err.data = data;
    throw err;
  }
  return data;
}

const STEPS = {
  LOADING: 'LOADING',
  PROCESSING: 'PROCESSING',
  OTP_INPUT: 'OTP_INPUT',
  FINALIZING: 'FINALIZING',
  COMPLETE: 'COMPLETE',
  BLOCKED: 'BLOCKED',
  ERROR: 'ERROR',
  MISSING_PARAM: 'MISSING_PARAM',
};

const SOURCE_REDIRECTS = {
  clearmotorclaims: 'https://clearmotorclaims.com/thank-you',
  clearcarclaims: 'https://clearcarclaims.com/thank-you',
  motorclaimhelp: 'http://motorclaimhelp.com/thank-you',
};

const LEAD_SOURCE_TO_SOURCE = {
  'Clear Motor Claims': 'clearmotorclaims',
  'Clear Car Claims': 'clearcarclaims',
  'Motor Claim Help': 'motorclaimhelp',
};

export default function PublicCreditCheckPage() {
  const [searchParams] = useSearchParams();
  const clientID = searchParams.get('clientID');
  const emailParam = searchParams.get('Email');
  const sourceParam = (searchParams.get('source') || '').toLowerCase().trim();
  const hasIdentifier = clientID || emailParam;

  // Email requires source parameter
  const missingSource = emailParam && !sourceParam;

  const [resolvedSource, setResolvedSource] = useState(sourceParam);
  const redirectUrl = SOURCE_REDIRECTS[resolvedSource] || null;

  const initialStep = !hasIdentifier ? STEPS.MISSING_PARAM : missingSource ? STEPS.MISSING_PARAM : STEPS.LOADING;
  const [step, setStep] = useState(initialStep);
  const [clientName, setClientName] = useState('');
  const [transactionId, setTransactionId] = useState(null);
  const [otpCode, setOtpCode] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [environment, setEnvironment] = useState(null);
  const [showClaimDetails, setShowClaimDetails] = useState(false);
  const [claims, setClaims] = useState([]);
  const [claimCount, setClaimCount] = useState(0);
  const [debugSteps, setDebugSteps] = useState([]);
  const [isStaging, setIsStaging] = useState(false);
  const [showDebug, setShowDebug] = useState(false);
  const [otpAttempts, setOtpAttempts] = useState(0);
  const MAX_OTP_ATTEMPTS = 5;
  const [processingMessage, setProcessingMessage] = useState({ title: 'Processing Your Credit Check', subtitle: 'Please wait while we verify your identity.' });
  const [redirectCountdown, setRedirectCountdown] = useState(null);

  const { sessionId, kountReady } = useKount(environment);
  const initStarted = useRef(false);

  // Load environment settings via public endpoint
  useEffect(() => {
    if (!hasIdentifier) return;
    fetch(`${BASE}/public/credit-check/environment`)
      .then(res => res.json())
      .then(data => {
        const env = data.environment || 'staging';
        setEnvironment(env);
        setIsStaging(env !== 'production');
        setShowDebug(data.showDebug === true);
      })
      .catch(() => { setEnvironment('staging'); setIsStaging(true); setShowDebug(true); });
  }, [hasIdentifier]);

  // Cycle through loading messages while processing
  useEffect(() => {
    if (step !== STEPS.PROCESSING) return;
    const messages = [
      { title: 'Processing Your Credit Check', subtitle: 'Please wait while we verify your identity.' },
      { title: 'Looking Up Your Record', subtitle: 'Searching our systems for your information...' },
      { title: 'Verifying Your Details', subtitle: 'Cross-referencing your records...' },
      { title: 'Almost There', subtitle: 'Finalizing the lookup process...' },
      { title: 'Still Working On It', subtitle: 'This is taking a little longer than usual. Please hold on.' },
      { title: 'Hang Tight', subtitle: 'We\'re making sure everything is in order.' },
      { title: 'Just a Moment', subtitle: 'Running final verification checks...' },
    ];
    let index = 0;
    setProcessingMessage(messages[0]);
    const interval = setInterval(() => {
      index = (index + 1) % messages.length;
      setProcessingMessage(messages[index]);
    }, 4000);
    return () => clearInterval(interval);
  }, [step]);

  // Auto-redirect when reaching a terminal state and source is provided
  useEffect(() => {
    const terminalSteps = [STEPS.COMPLETE, STEPS.BLOCKED, STEPS.ERROR];
    if (!redirectUrl || !terminalSteps.includes(step)) return;

    const REDIRECT_SECONDS = 5;
    setRedirectCountdown(REDIRECT_SECONDS);

    const interval = setInterval(() => {
      setRedirectCountdown(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          window.location.href = redirectUrl;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [step, redirectUrl]);

  // Start credit check once Kount is ready
  useEffect(() => {
    if (!hasIdentifier || !kountReady || initStarted.current) return;
    initStarted.current = true;
    startCreditCheck();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasIdentifier, kountReady]);

  async function startCreditCheck() {
    setStep(STEPS.PROCESSING);
    setError(null);
    setDebugSteps([]);

    try {
      const initBody = clientID
        ? { clientID, deviceSessionId: sessionId }
        : { email: emailParam, deviceSessionId: sessionId };
      const result = await publicRequest('/public/credit-check/init', initBody);

      setClientName(result.clientName || '');
      setTransactionId(result.transactionId || null);
      if (result.debugSteps) setDebugSteps(result.debugSteps);

      // Resolve source from Lead_Source when using clientID (no source param)
      if (!sourceParam && result.leadSource && LEAD_SOURCE_TO_SOURCE[result.leadSource]) {
        setResolvedSource(LEAD_SOURCE_TO_SOURCE[result.leadSource]);
      }

      if (result.decision === 'ALLOW' && result.creditCheckComplete) {
        setShowClaimDetails(result.showClaimDetails || false);
        setClaims(result.claims || []);
        setClaimCount(result.claimCount || 0);
        setStep(STEPS.COMPLETE);
      } else if (result.decision === 'NOT_FOUND') {
        setError(result.message || "We couldn't find your record at this time. We'll be in touch shortly.");
        setStep(STEPS.ERROR);
      } else if (result.decision === 'CHALLENGE') {
        setStep(STEPS.OTP_INPUT);
      } else if (result.decision === 'BLOCK') {
        setStep(STEPS.BLOCKED);
      }
    } catch (err) {
      if (err.debugSteps && err.debugSteps.length > 0) setDebugSteps(err.debugSteps);
      // Resolve source from leadSource in error responses too
      if (!sourceParam && err.leadSource && LEAD_SOURCE_TO_SOURCE[err.leadSource]) {
        setResolvedSource(LEAD_SOURCE_TO_SOURCE[err.leadSource]);
      }
      if (err.alreadyProcessed) {
        setClientName(err.clientName || clientName);
        setError('This client has already been processed. Please contact support if you need to run again.');
      } else if (err.alreadyPooled) {
        setClientName(err.clientName || clientName);
        setError('Your credit check is already being processed. Your claims will be created shortly.');
      } else {
        setError(err.message);
      }
      setStep(STEPS.ERROR);
    }
  }

  async function handleOtpSubmit(e) {
    e.preventDefault();
    if (!otpCode.trim() || loading) return;

    if (otpAttempts >= MAX_OTP_ATTEMPTS) {
      setError(`Maximum verification attempts (${MAX_OTP_ATTEMPTS}) exceeded. Please try again later.`);
      setStep(STEPS.ERROR);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await publicRequest('/public/credit-check/verify-otp', {
        transactionId,
        code: otpCode.trim(),
        clientID
      });

      if (result.debugSteps) setDebugSteps(prev => [...prev, ...result.debugSteps]);

      if (result.otpStatus === 'APPROVED' && result.creditCheckComplete) {
        setShowClaimDetails(result.showClaimDetails || false);
        setClaims(result.claims || []);
        setClaimCount(result.claimCount || 0);
        setStep(STEPS.COMPLETE);
      } else {
        const newAttempts = otpAttempts + 1;
        setOtpAttempts(newAttempts);
        setOtpCode('');
        if (newAttempts >= MAX_OTP_ATTEMPTS) {
          setError(`Maximum verification attempts (${MAX_OTP_ATTEMPTS}) exceeded. Please try again later.`);
          setStep(STEPS.ERROR);
        } else {
          setError(`Incorrect code. ${MAX_OTP_ATTEMPTS - newAttempts} attempt${MAX_OTP_ATTEMPTS - newAttempts !== 1 ? 's' : ''} remaining.`);
        }
      }
    } catch (err) {
      if (err.debugSteps && err.debugSteps.length > 0) setDebugSteps(prev => [...prev, ...err.debugSteps]);
      const newAttempts = otpAttempts + 1;
      setOtpAttempts(newAttempts);
      setOtpCode('');
      if (newAttempts >= MAX_OTP_ATTEMPTS) {
        setError(`Maximum verification attempts (${MAX_OTP_ATTEMPTS}) exceeded. Please try again later.`);
        setStep(STEPS.ERROR);
      } else {
        setError(`Verification failed. ${MAX_OTP_ATTEMPTS - newAttempts} attempt${MAX_OTP_ATTEMPTS - newAttempts !== 1 ? 's' : ''} remaining.`);
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleResendOtp() {
    if (resending) return;
    setResending(true);
    setError(null);

    try {
      await publicRequest('/public/credit-check/resend-otp', {
        transactionId,
        clientID
      });
      setOtpCode('');
    } catch (err) {
      setError(err.message);
    } finally {
      setResending(false);
    }
  }

  return (
    <div className="public-page">
      <div className="public-card">
        <h1 className="public-title">Credit Check</h1>
        <p className="public-subtitle">Secure identity verification</p>

        {step === STEPS.MISSING_PARAM && (
          <>
            <div className="public-error-icon">!</div>
            <p className="public-status-text">Missing Required Parameter</p>
            <p className="public-status-subtext">
              {missingSource
                ? 'A required parameter (source) is missing from the URL. Please use the link provided to you to access this page.'
                : 'A required parameter (clientID or Email) is missing from the URL. Please use the link provided to you to access this page.'}
            </p>
          </>
        )}

        {step === STEPS.LOADING && (
          <>
            <div className="public-spinner" />
            <p className="public-status-text">Initializing...</p>
            <p className="public-status-subtext">Setting up secure connection</p>
          </>
        )}

        {step === STEPS.PROCESSING && (
          <>
            <div className="public-spinner" />
            <p className="public-status-text">{processingMessage.title}</p>
            <p className="public-status-subtext">{processingMessage.subtitle}</p>
          </>
        )}

        {step === STEPS.OTP_INPUT && (
          <>
            {clientName && <p className="public-client-name">{clientName}</p>}
            <p className="public-status-text">Verification Required</p>
            <div className="public-divider" />

            {error && <div className="public-error-msg">{error}</div>}

            <form onSubmit={handleOtpSubmit}>
              <p className="public-otp-label">
                A one-time verification code has been sent to your registered contact details.
              </p>
              <p className="public-otp-hint">
                Enter the code below to continue your credit check.
                {otpAttempts > 0 && (
                  <span style={{ display: 'block', marginTop: '0.5rem', color: '#f59e0b', fontWeight: 600, fontSize: '0.82rem' }}>
                    {MAX_OTP_ATTEMPTS - otpAttempts} attempt{MAX_OTP_ATTEMPTS - otpAttempts !== 1 ? 's' : ''} remaining
                  </span>
                )}
              </p>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={8}
                placeholder="Enter code"
                value={otpCode}
                onChange={e => setOtpCode(e.target.value.replace(/[^0-9]/g, ''))}
                className="public-otp-input"
                autoFocus
              />
              <button
                type="submit"
                disabled={!otpCode.trim() || loading}
                className="public-btn-primary"
              >
                {loading ? 'Verifying...' : 'Submit Code'}
              </button>
            </form>
            <button
              onClick={handleResendOtp}
              disabled={resending}
              className="public-btn-link"
            >
              {resending ? 'Sending...' : "Didn't receive a code? Resend"}
            </button>
          </>
        )}

        {step === STEPS.FINALIZING && (
          <>
            <div className="public-spinner" />
            <p className="public-status-text">Finalizing Your Credit Check</p>
            <p className="public-status-subtext">Almost done, please hold on...</p>
          </>
        )}

        {step === STEPS.COMPLETE && (
          <>
            <div className="public-checkmark">&#10003;</div>
            {clientName && <p className="public-client-name">{clientName}</p>}
            <p className="public-status-text">Credit Check Complete</p>

            {showClaimDetails ? (
              <>
                <div className="public-divider" />
                <p className="public-claim-summary">
                  {claimCount === 0
                    ? 'No claims were found on your credit file.'
                    : `${claimCount} claim${claimCount !== 1 ? 's' : ''} found on your credit file.`}
                </p>
                <div className="public-divider" />
                <p className="public-status-subtext">
                  {redirectUrl && redirectCountdown !== null
                    ? `Redirecting in ${redirectCountdown} second${redirectCountdown !== 1 ? 's' : ''}...`
                    : 'You may now close this page.'}
                </p>
              </>
            ) : (
              <p className="public-status-subtext">
                {redirectUrl && redirectCountdown !== null
                  ? `Redirecting in ${redirectCountdown} second${redirectCountdown !== 1 ? 's' : ''}...`
                  : 'Your credit check has been successfully processed. You may now close this page.'}
              </p>
            )}
          </>
        )}

        {step === STEPS.BLOCKED && (
          <>
            <div className="public-blocked-icon">!</div>
            {clientName && <p className="public-client-name">{clientName}</p>}
            <p className="public-status-text">Verification Could Not Be Completed</p>
            <p className="public-status-subtext">
              {redirectUrl && redirectCountdown !== null
                ? `We were unable to verify your identity. Redirecting in ${redirectCountdown} second${redirectCountdown !== 1 ? 's' : ''}...`
                : 'We were unable to verify your identity at this time. Please contact support for assistance.'}
            </p>
          </>
        )}

        {step === STEPS.ERROR && (
          <>
            <div className="public-error-icon">&#10005;</div>
            <p className="public-status-text">Something Went Wrong</p>
            {error && <div className="public-error-msg">{error}</div>}
            <p className="public-status-subtext">
              {redirectUrl && redirectCountdown !== null
                ? `Redirecting in ${redirectCountdown} second${redirectCountdown !== 1 ? 's' : ''}...`
                : 'Please try again or contact support if the issue persists.'}
            </p>
          </>
        )}
      </div>

      {showDebug && debugSteps.length > 0 && (
        <div className="public-debug-panel">
          <div className="public-debug-header">
            <span className="public-debug-badge">STAGING DEBUG</span>
            <span>Step-by-step processing details</span>
          </div>
          <ProcessingTimeline steps={debugSteps} />
        </div>
      )}
    </div>
  );
}
